import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert, Box, Button, Card, CardActions, CardContent, Checkbox, Chip,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, FormControlLabel, Grid, IconButton, InputLabel,
  MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  Add, Close, Delete, Download, Edit, PlayArrow, TableChart, Visibility,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { manufacturingAPI } from '../../services/api';
import { toast } from 'react-toastify';

const OP_LABELS = {
  contains: 'contains',
  equals: 'is',
  not_equals: 'is not',
  starts_with: 'starts with',
  gte: 'on or after / ≥',
  lte: 'on or before / ≤',
  on: 'on',
};

const emptyDraft = {
  id: null, name: '', description: '', dataset: '',
  columns: [], filters: [], sort: null,
};

const CustomReportsSection = () => {
  const [meta, setMeta] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Builder state
  const [builderOpen, setBuilderOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [builderPreview, setBuilderPreview] = useState(null);
  const [builderPreviewLoading, setBuilderPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Run dialog state
  const [runTarget, setRunTarget] = useState(null);
  const [runData, setRunData] = useState(null);
  const [runLoading, setRunLoading] = useState(false);
  const [runRefreshedAt, setRunRefreshedAt] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [metaRes, listRes] = await Promise.all([
        manufacturingAPI.customReports.datasets(),
        manufacturingAPI.customReports.list(),
      ]);
      setMeta(metaRes.data);
      setReports(listRes.data.reports || []);
    } catch (error) {
      console.error('Failed to load custom reports:', error);
      toast.error('Failed to load custom reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const datasetMeta = meta?.datasets?.find((d) => d.key === draft.dataset) || null;
  const fieldByKey = (key) => datasetMeta?.fields?.find((f) => f.key === key) || null;
  const opsForField = (field) => (field ? (meta?.operators_by_type?.[field.type] || []) : []);

  // --- Builder helpers ---
  const openBuilderNew = () => {
    setDraft(emptyDraft);
    setBuilderPreview(null);
    setBuilderOpen(true);
  };

  const openBuilderEdit = (report) => {
    setDraft({
      id: report.id,
      name: report.name,
      description: report.description || '',
      dataset: report.dataset,
      columns: report.config?.columns || [],
      filters: report.config?.filters || [],
      sort: report.config?.sort || null,
    });
    setBuilderPreview(null);
    setBuilderOpen(true);
  };

  const handleDatasetChange = (key) => {
    const ds = meta?.datasets?.find((d) => d.key === key);
    setDraft((prev) => ({
      ...prev,
      dataset: key,
      columns: ds ? ds.fields.map((f) => f.key) : [],
      filters: [],
      sort: ds?.default_sort || null,
    }));
    setBuilderPreview(null);
  };

  const toggleColumn = (key) => {
    setDraft((prev) => {
      const on = prev.columns.includes(key);
      const all = datasetMeta?.fields?.map((f) => f.key) || [];
      // Keep registry order regardless of tick order.
      const next = all.filter((k) => (k === key ? !on : prev.columns.includes(k)));
      return { ...prev, columns: next };
    });
  };

  const addFilter = () => {
    const first = datasetMeta?.fields?.[0];
    if (!first) return;
    const ops = opsForField(first);
    setDraft((prev) => ({
      ...prev,
      filters: [...prev.filters, { field: first.key, op: ops[0] || 'equals', value: '' }],
    }));
  };

  const updateFilter = (idx, patch) => {
    setDraft((prev) => ({
      ...prev,
      filters: prev.filters.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    }));
  };

  const removeFilter = (idx) => {
    setDraft((prev) => ({ ...prev, filters: prev.filters.filter((_, i) => i !== idx) }));
  };

  const draftConfig = () => ({
    columns: draft.columns,
    filters: draft.filters.filter((f) => f.value !== '' && f.value !== null),
    sort: draft.sort || datasetMeta?.default_sort,
  });

  const draftValid = draft.name.trim() && draft.dataset && draft.columns.length > 0;

  const handleBuilderPreview = async () => {
    setBuilderPreviewLoading(true);
    try {
      const response = await manufacturingAPI.customReports.run({
        dataset: draft.dataset,
        config: draftConfig(),
      });
      setBuilderPreview(response.data);
    } catch (error) {
      console.error('Custom report preview failed:', error);
      setBuilderPreview({ error: error.response?.data?.error || 'Failed to load preview' });
    } finally {
      setBuilderPreviewLoading(false);
    }
  };

  // Live preview: re-run automatically (debounced) whenever the report
  // definition changes while the builder is open.
  const previewTimer = useRef(null);
  useEffect(() => {
    if (!builderOpen || !draft.dataset || draft.columns.length === 0) return undefined;
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(handleBuilderPreview, 600);
    return () => clearTimeout(previewTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderOpen, draft.dataset, draft.columns, draft.filters, draft.sort]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim(),
        dataset: draft.dataset,
        config: draftConfig(),
      };
      if (draft.id) {
        await manufacturingAPI.customReports.update(draft.id, payload);
        toast.success('Custom report updated');
      } else {
        await manufacturingAPI.customReports.create(payload);
        toast.success('Custom report saved');
      }
      setBuilderOpen(false);
      loadAll();
    } catch (error) {
      console.error('Failed to save custom report:', error);
      toast.error(error.response?.data?.error || 'Failed to save custom report');
    } finally {
      setSaving(false);
    }
  };

  // --- Run / download / delete ---
  const fetchRunData = async (report, silent = false) => {
    if (!silent) setRunLoading(true);
    try {
      const response = await manufacturingAPI.customReports.run({ report_id: report.id });
      setRunData(response.data);
      setRunRefreshedAt(new Date());
    } catch (error) {
      console.error('Failed to run custom report:', error);
      if (!silent) setRunData({ error: error.response?.data?.error || 'Failed to run report' });
    } finally {
      if (!silent) setRunLoading(false);
    }
  };

  const handleRun = (report) => {
    setRunTarget(report);
    setRunData(null);
    setRunRefreshedAt(null);
    fetchRunData(report);
  };

  // Auto-refresh the open run dialog every 30s so it tracks data changes.
  useEffect(() => {
    if (!runTarget) return undefined;
    const id = setInterval(() => fetchRunData(runTarget, true), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runTarget]);

  const handleDownload = async (report) => {
    setDownloadingId(report.id);
    try {
      const response = await manufacturingAPI.customReports.download(report.id);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safe = report.name.replace(/[^\w\s-]/g, '_').trim().replace(/\s+/g, '_');
      link.setAttribute('download', `${safe || 'Custom_Report'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      console.error('Failed to download custom report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await manufacturingAPI.customReports.remove(deleteTarget.id);
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
      loadAll();
    } catch (error) {
      console.error('Failed to delete custom report:', error);
      toast.error('Failed to delete report');
    } finally {
      setDeleting(false);
    }
  };

  // --- Render helpers ---
  const renderCell = (value, colType) => {
    if (value === null || value === undefined) return '';
    if (colType === 'boolean') return value ? 'Yes' : 'No';
    if (colType === 'number') return Number(value).toLocaleString();
    return String(value);
  };

  const renderResultTable = (data) => (
    <>
      <TableContainer component={Paper} variant="outlined" className="keep-table" sx={{ maxHeight: 380 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {data.columns.map((c) => (
                <TableCell key={c.key} align={c.type === 'number' ? 'right' : 'left'}>
                  <strong>{c.label}</strong>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((row, i) => (
              <TableRow key={i}>
                {row.map((v, j) => (
                  <TableCell key={j} align={data.columns[j]?.type === 'number' ? 'right' : 'left'}>
                    {renderCell(v, data.columns[j]?.type)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {data.total_rows?.toLocaleString()} row{data.total_rows === 1 ? '' : 's'} total
          {data.truncated ? ` - showing first ${data.rows.length}` : ''}
        </Typography>
      </Box>
    </>
  );

  const renderFilterValueInput = (filter, idx) => {
    const field = fieldByKey(filter.field);
    if (!field) return null;
    if (field.type === 'date') {
      return (
        <DatePicker
          label="Value"
          value={filter.value ? new Date(filter.value) : null}
          onChange={(newValue) => updateFilter(idx, {
            value: newValue ? format(newValue, 'yyyy-MM-dd') : '',
          })}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
      );
    }
    if (field.type === 'choice') {
      return (
        <FormControl size="small" fullWidth>
          <InputLabel>Value</InputLabel>
          <Select
            label="Value"
            value={filter.value}
            onChange={(e) => updateFilter(idx, { value: e.target.value })}
          >
            {(field.choices || []).map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (field.type === 'boolean') {
      return (
        <FormControl size="small" fullWidth>
          <InputLabel>Value</InputLabel>
          <Select
            label="Value"
            value={filter.value === '' ? '' : String(filter.value)}
            onChange={(e) => updateFilter(idx, { value: e.target.value === 'true' })}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      );
    }
    return (
      <TextField
        label="Value"
        size="small"
        fullWidth
        type={field.type === 'number' ? 'number' : 'text'}
        value={filter.value}
        onChange={(e) => updateFilter(idx, { value: e.target.value })}
      />
    );
  };

  return (
    <Box sx={{ mt: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Custom Reports</Typography>
          <Typography variant="body2" color="text.secondary">
            Build your own report: pick a dataset, choose columns and filters, then save it here.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openBuilderNew}>
          New Custom Report
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>
      ) : reports.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <TableChart sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            No custom reports yet - click "New Custom Report" to build one.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {reports.map((report) => (
            <Grid item xs={12} md={6} lg={4} key={report.id}>
              <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h6" fontWeight={600}>{report.name}</Typography>
                    <Box>
                      <IconButton size="small" onClick={() => openBuilderEdit(report)} title="Edit">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(report)} title="Delete">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, my: 1, flexWrap: 'wrap' }}>
                    <Chip label={report.dataset_label} size="small" color="secondary" variant="outlined" />
                    <Chip label={`${report.config?.columns?.length || 0} columns`} size="small" variant="outlined" />
                    {(report.config?.filters?.length || 0) > 0 && (
                      <Chip label={`${report.config.filters.length} filter${report.config.filters.length === 1 ? '' : 's'}`} size="small" variant="outlined" />
                    )}
                  </Box>
                  {report.description && (
                    <Typography variant="body2" color="text.secondary">{report.description}</Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                    Updated {report.updated_at}{report.created_by ? ` · ${report.created_by}` : ''}
                  </Typography>
                </CardContent>
                <Divider />
                <CardActions sx={{ p: 1.5 }}>
                  <Button size="small" startIcon={<PlayArrow />} onClick={() => handleRun(report)}>
                    Run
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={downloadingId === report.id ? <CircularProgress size={16} color="inherit" /> : <Download />}
                    onClick={() => handleDownload(report)}
                    disabled={downloadingId === report.id}
                  >
                    {downloadingId === report.id ? 'Generating...' : 'Download'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Builder dialog */}
      <Dialog open={builderOpen} onClose={() => setBuilderOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {draft.id ? 'Edit Custom Report' : 'New Custom Report'}
            </Typography>
            <IconButton onClick={() => setBuilderOpen(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Report name"
                fullWidth
                required
                value={draft.name}
                onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Description (optional)"
                fullWidth
                value={draft.description}
                onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Data source</InputLabel>
                <Select
                  label="Data source"
                  value={draft.dataset}
                  onChange={(e) => handleDatasetChange(e.target.value)}
                >
                  {(meta?.datasets || []).map((d) => (
                    <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {datasetMeta ? (
            <>
              <Typography variant="caption" color="text.secondary">{datasetMeta.description}</Typography>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Columns</Typography>
              <Grid container>
                {datasetMeta.fields.map((f) => (
                  <Grid item xs={6} sm={4} md={3} key={f.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={draft.columns.includes(f.key)}
                          onChange={() => toggleColumn(f.key)}
                        />
                      }
                      label={<Typography variant="body2">{f.label}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Filters</Typography>
                <Button size="small" startIcon={<Add />} onClick={addFilter}>Add filter</Button>
              </Box>
              {draft.filters.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No filters - the report will include every row.
                </Typography>
              )}
              {draft.filters.map((filter, idx) => {
                const field = fieldByKey(filter.field);
                return (
                  <Grid container spacing={1} alignItems="center" key={idx} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={4}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Field</InputLabel>
                        <Select
                          label="Field"
                          value={filter.field}
                          onChange={(e) => {
                            const nf = fieldByKey(e.target.value);
                            updateFilter(idx, {
                              field: e.target.value,
                              op: opsForField(nf)[0] || 'equals',
                              value: '',
                            });
                          }}
                        >
                          {datasetMeta.fields.map((f) => (
                            <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Condition</InputLabel>
                        <Select
                          label="Condition"
                          value={filter.op}
                          onChange={(e) => updateFilter(idx, { op: e.target.value })}
                        >
                          {opsForField(field).map((op) => (
                            <MenuItem key={op} value={op}>{OP_LABELS[op] || op}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={10} sm={4}>
                      {renderFilterValueInput(filter, idx)}
                    </Grid>
                    <Grid item xs={2} sm={1}>
                      <IconButton size="small" color="error" onClick={() => removeFilter(idx)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Sort</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Sort by</InputLabel>
                    <Select
                      label="Sort by"
                      value={draft.sort?.field || datasetMeta.default_sort.field}
                      onChange={(e) => setDraft((prev) => ({
                        ...prev,
                        sort: { field: e.target.value, direction: prev.sort?.direction || 'asc' },
                      }))}
                    >
                      {datasetMeta.fields.map((f) => (
                        <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Direction</InputLabel>
                    <Select
                      label="Direction"
                      value={draft.sort?.direction || datasetMeta.default_sort.direction}
                      onChange={(e) => setDraft((prev) => ({
                        ...prev,
                        sort: {
                          field: prev.sort?.field || datasetMeta.default_sort.field,
                          direction: e.target.value,
                        },
                      }))}
                    >
                      <MenuItem value="asc">Ascending</MenuItem>
                      <MenuItem value="desc">Descending</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography variant="subtitle2">Preview</Typography>
                {builderPreviewLoading && <CircularProgress size={14} />}
                <Typography variant="caption" color="text.secondary">
                  updates automatically as you change the report
                </Typography>
                <Button
                  size="small"
                  onClick={handleBuilderPreview}
                  disabled={builderPreviewLoading || draft.columns.length === 0}
                  startIcon={<Visibility />}
                >
                  Refresh now
                </Button>
              </Box>

              <Box>
                {builderPreview?.error ? (
                  <Alert severity="error">{builderPreview.error}</Alert>
                ) : builderPreview ? (
                  renderResultTable(builderPreview)
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={22} />
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <TableChart sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                Choose a data source to start building your report.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuilderOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!draftValid || saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : draft.id ? 'Save Changes' : 'Save Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run dialog */}
      <Dialog open={!!runTarget} onClose={() => setRunTarget(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">{runTarget?.name}</Typography>
              {runTarget?.description && (
                <Typography variant="caption" color="text.secondary">{runTarget.description}</Typography>
              )}
            </Box>
            <IconButton onClick={() => setRunTarget(null)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {runLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : runData?.error ? (
            <Alert severity="error">{runData.error}</Alert>
          ) : runData ? (
            <>
              {renderResultTable(runData)}
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                Auto-refreshes every 30 seconds
                {runRefreshedAt ? ` - last updated ${runRefreshedAt.toLocaleTimeString('en-GB')}` : ''}
              </Typography>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<PlayArrow />}
            onClick={() => runTarget && fetchRunData(runTarget, true)}
          >
            Refresh
          </Button>
          <Button onClick={() => setRunTarget(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={downloadingId === runTarget?.id ? <CircularProgress size={20} color="inherit" /> : <Download />}
            onClick={() => runTarget && handleDownload(runTarget)}
            disabled={!runTarget || downloadingId === runTarget?.id}
          >
            {downloadingId === runTarget?.id ? 'Generating...' : 'Download Full Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete custom report?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{deleteTarget?.name}" will be permanently deleted. This only removes the saved
            report definition - no data is affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomReportsSection;
